import { createSlice } from '@reduxjs/toolkit';
import { notify } from 'components/Common/Toast';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { parishSelectionMoved, selectSelectedParishId } from 'redux/selectors/parishContext';
import { RootState } from 'redux/store';
import { fetchMember, updateMember } from 'services/membersApi';
import { ParishMemberDetailState } from 'types/community';
import type { Member, UpdateMemberPayload } from 'types/members';
import { getProblemDetail } from 'api/v1Helpers';

type DetailState = ParishMemberDetailState & {
    details: Member | Record<string, never>;
    inflightParishId: string | null;
    inflightUserId: string | null;
};

const initialState: DetailState = {
    details: {},
    loading: false,
    error: null,
    inflightParishId: null,
    inflightUserId: null,
};

interface FetchParishMemberDetails {
    /** Route param — treated as userId (UUID) on /v1. */
    uuid: string;
    parishId: string;
}

interface UpdateParishionerDetailsPayload {
    userId: string;
    payload: UpdateMemberPayload;
}

export const fetchParishMemberDetails = createAsyncThunkWrapper<
    { member: Member; parishId: string; userId: string } | null,
    FetchParishMemberDetails
>(
    'parishMemberDetails/fetchParishMemberDetails',
    async ({ uuid, parishId }, { getState }) => {
        if (!parishId || !uuid) return null;
        try {
            const member = await fetchMember(parishId, uuid);
            if (parishSelectionMoved(getState, parishId)) return null;
            return { member, parishId, userId: uuid };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load member details' }
);

export const updateParishionerDetails = createAsyncThunkWrapper<Member | null, UpdateParishionerDetailsPayload>(
    'parishMemberDetails/updateMemberInfo',
    async ({ userId, payload }, { getState }) => {
        const parishId = selectSelectedParishId(getState() as RootState);
        if (!parishId || !userId) return null;
        const result = await updateMember(parishId, userId, payload);
        notify('Parishioner details updated successfully', 'success');
        return result;
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to update member details' }
);

const parishMemberDetailsSlice = createSlice({
    name: 'parishMemberDetails',
    initialState,
    reducers: {
        clearParishMemberDetails(state) {
            state.details = {};
            state.error = null;
            state.inflightParishId = null;
            state.inflightUserId = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchParishMemberDetails.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.details = {};
            state.inflightParishId = action.meta.arg.parishId;
            state.inflightUserId = action.meta.arg.uuid;
        });
        builder.addCase(fetchParishMemberDetails.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightParishId === action.meta.arg.parishId && state.inflightUserId === action.meta.arg.uuid) {
                    state.loading = false;
                }
                return;
            }
            const { member, parishId, userId } = action.payload;
            if (parishId !== state.inflightParishId || userId !== state.inflightUserId) return;
            state.loading = false;
            state.details = member;
        });
        builder.addCase(fetchParishMemberDetails.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId && state.inflightUserId === action.meta.arg.uuid) {
                state.loading = false;
                state.error = action.error.message || 'Failed to load member details';
            }
        });
        builder.addCase(updateParishionerDetails.fulfilled, (state, action) => {
            if (action.payload) {
                state.details = action.payload;
            }
        });
    },
});

export const { clearParishMemberDetails } = parishMemberDetailsSlice.actions;
export default parishMemberDetailsSlice.reducer;
